import * as React from "react"
import { Send } from "lucide-react"
import { useServerFn } from "@tanstack/react-start"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { submitInquiryFn } from "@/lib/app.functions"
import type { User } from "@/lib/types"

export function InquiryForm({
  propertyId,
  propertyTitle,
  viewer,
  defaultMessage,
}: {
  propertyId?: number
  propertyTitle?: string
  viewer?: User | null
  defaultMessage?: string
}) {
  const submitInquiry = useServerFn(submitInquiryFn)
  const [pending, setPending] = React.useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPending(true)
    const form = new FormData(event.currentTarget)
    try {
      await submitInquiry({
        data: {
          propertyId: propertyId ?? null,
          name: String(form.get("name") ?? ""),
          email: String(form.get("email") ?? ""),
          phone: String(form.get("phone") ?? ""),
          message: String(form.get("message") ?? ""),
        },
      })
      event.currentTarget.reset()
      toast.success("Your inquiry has been added to the demo inbox.")
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Unable to send inquiry."
      )
    } finally {
      setPending(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Name" htmlFor="inquiry-name">
          <Input
            id="inquiry-name"
            name="name"
            required
            minLength={2}
            defaultValue={viewer?.name}
            className="field-input"
          />
        </FormField>
        <FormField label="Email" htmlFor="inquiry-email">
          <Input
            id="inquiry-email"
            name="email"
            type="email"
            required
            defaultValue={viewer?.email}
            className="field-input"
          />
        </FormField>
      </div>
      <FormField label="Phone" htmlFor="inquiry-phone">
        <Input
          id="inquiry-phone"
          name="phone"
          type="tel"
          defaultValue={viewer?.phone}
          className="field-input"
        />
      </FormField>
      <FormField label="Message" htmlFor="inquiry-message">
        <Textarea
          id="inquiry-message"
          name="message"
          required
          minLength={10}
          rows={5}
          defaultValue={
            defaultMessage ??
            (propertyTitle
              ? `I would like to learn more about ${propertyTitle}.`
              : "")
          }
          className="rounded-none bg-white"
        />
      </FormField>
      <p className="text-xs leading-5 text-muted-foreground">
        Demo only. This message is stored in the fictional administration inbox
        and is not delivered externally.
      </p>
      <Button type="submit" className="field-button w-full" disabled={pending}>
        <Send />
        {pending ? "Sending…" : "Send inquiry"}
      </Button>
    </form>
  )
}

function FormField({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="field-label">
        {label}
      </Label>
      {children}
    </div>
  )
}
